import { TextLayout } from '@st/document-rendering'
import {
  ElementStatus,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'
import type { NewAnnotation } from '@st/document-viewer-kit/plugins/annotations'
import { toDragPointerEvent, useUpdateBounds } from '@st/document-viewer-kit'
import { move, Offset, Size } from '@st/geom'
import { TextAnnotation } from '@st/sdk'
import { FontSizeLargerIcon, FontSizeSmallerIcon } from '@st/theme'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'
import { DeleteIcon } from '~/components/icons'
import { DocumentAnnotationDisplay } from '~/st-doc-viewer/document-annotation-display'
import { TextToolState } from '../../annotations'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'
import { InlineColorPicker } from '../../inline-color-picker'
import { copyToolState, TEXT_COLOR_OPTIONS } from './tool-state'

const LINE_HEIGHT = 1.1

export function TextToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: TextToolState
  activeAnnotation: TextAnnotation | undefined
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  return (
    <InlineColorPicker
      options={TEXT_COLOR_OPTIONS}
      value={toolState.settings.color}
      onChange={(color) => {
        if (activeAnnotation) {
          const newAnnotation: TextAnnotation = {
            ...activeAnnotation,
            color: color
          }
          send({ type: 'updateAnnotationDraft', annotationDraft: newAnnotation })
          send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
        } else {
          send({
            type: 'updateTool',
            tool: { ...toolState, settings: { ...toolState.settings, color: color } }
          })
        }
      }}
    />
  )
}

export function TextToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: TextToolState
  activeAnnotation: TextAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  const transform = useLinearTransform()

  return (
    <div
      style={{
        position: 'absolute',
        cursor: 'text',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

        if (activeAnnotation) {
          send({ type: 'deselectAnnotation' })
          return
        } else {
          send({
            type: 'requestAnnotationDraft',
            elementStatus: 'editing',
            annotationDraft: autoResizeAnnotation(
              {
                type: 'text',
                pageNumber: page.pageNumber,
                content: '',
                bounds: {
                  x: pointerEvent.point.x,
                  y: pointerEvent.point.y,
                  width: 0,
                  height: 0
                },
                color: toolState.settings.color,
                font: toolState.settings.font
              },
              { lineHeight: LINE_HEIGHT }
            )
          })
        }
      }}
    />
  )
}

export function TextAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: TextAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  switch (status) {
    case 'selected':
      return <TextAnnotationSelected annotation={annotation} send={send} />
    case 'editing':
      return <TextAnnotationEditing annotation={annotation} send={send} />
    case 'none':
      return (
        <TextAnnotationView
          annotation={annotation}
          onSelect={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        />
      )
  }
}

function TextAnnotationView({
  annotation,
  onSelect
}: {
  annotation: TextAnnotation
  onSelect: () => void
}) {
  return (
    <ViewElementContainer key={annotation.id} bounds={annotation.bounds} onClick={onSelect}>
      <TextAnnotationDisplay annotation={annotation} />
    </ViewElementContainer>
  )
}

function TextAnnotationDisplay({ annotation }: { annotation: TextAnnotation }) {
  return <DocumentAnnotationDisplay annotation={annotation} />
}

function TextAnnotationSelected({
  annotation,
  send
}: {
  annotation: TextAnnotation
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  const { bounds, onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  function handleNudge(offset: Partial<Offset>) {
    send({
      type: 'updateAnnotationDraft',
      annotationDraft: { ...annotation, bounds: move(annotation.bounds, offset) }
    })
  }

  useKeyboardShortcuts({
    Backspace: () => {
      send({ type: 'deleteAnnotation', annotationId: annotation.id })
    },
    ArrowUp: () => handleNudge({ dy: -5 }),
    ArrowDown: () => handleNudge({ dy: 5 }),
    ArrowLeft: () => handleNudge({ dx: -5 }),
    ArrowRight: () => handleNudge({ dx: 5 })
  })

  return (
    <SelectedElementContainer
      bounds={bounds}
      onMove={onMove}
      resizable={false}
      onDoubleClick={() => {
        send({ type: 'beginEditingAnnotation', annotationDraft: annotation })
      }}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<FontSizeSmallerIcon className="size=6" />}
            onClick={() => {
              const newAnnotation: TextAnnotation = autoResizeAnnotation(
                {
                  ...annotation,
                  font: { ...annotation.font, size: annotation.font.size - 2 }
                },
                { lineHeight: LINE_HEIGHT }
              )
              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<FontSizeLargerIcon className="size=6" />}
            onClick={() => {
              const newAnnotation: TextAnnotation = autoResizeAnnotation(
                {
                  ...annotation,
                  font: { ...annotation.font, size: annotation.font.size + 2 }
                },
                { lineHeight: LINE_HEIGHT }
              )

              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => send({ type: 'deleteAnnotation', annotationId: annotation.id })}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <TextAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

function TextAnnotationEditing({
  annotation,
  send
}: {
  annotation: TextAnnotation
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  return (
    <ViewElementContainer key={annotation.id} bounds={annotation.bounds}>
      <textarea
        value={annotation.content}
        autoFocus={true}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        wrap="off"
        style={{
          border: 'none',
          padding: 0,
          outline: 'none',
          background: 'none',
          resize: 'none',
          overflow: 'hidden',
          fontKerning: 'none',
          boxSizing: 'content-box',
          whiteSpace: 'pre',
          width: annotation.bounds.width,
          height: annotation.bounds.height,
          fontFamily: annotation.font.family,
          fontWeight: annotation.font.weight,
          fontSize: annotation.font.size,
          color: annotation.color,
          lineHeight: LINE_HEIGHT
        }}
        onChange={(e) => {
          const newText = e.target.value

          const nextAnnotation: TextAnnotation = autoResizeAnnotation(
            {
              ...annotation,
              content: newText
            },
            { lineHeight: LINE_HEIGHT }
          )
          send({
            type: 'updateAnnotationDraft',
            annotationDraft: nextAnnotation
          })
        }}
      />
    </ViewElementContainer>
  )
}

function calculateTextDimensions(
  text: string,
  { font, lineHeight }: { font: TextAnnotation['font']; lineHeight: number }
): Size {
  return TextLayout.layout(text || '  ', font, lineHeight)
}

function autoResizeAnnotation<A extends TextAnnotation | NewAnnotation<TextAnnotation>>(
  annot: A,
  { lineHeight }: { lineHeight: number }
): A {
  const size = calculateTextDimensions(annot.content, {
    font: annot.font,
    lineHeight: lineHeight
  })
  return {
    ...annot,
    bounds: {
      x: annot.bounds.x,
      y: annot.bounds.y,
      width: size.width,
      height: size.height
    }
  } as A
}
